// v16.93 (06/2026) — OFFLINE-FIRST sync engine cho trọng tài (bật lại theo
// yêu cầu user: trọng tài điều hành xuyên suốt trận dù mất mạng/backend nghẽn).
//
// Replay tuần tự (FIFO nghiêm ngặt theo local_ts) mọi mutation đã queue trong
// IndexedDB với CÙNG Idempotency-Key → BE dedupe qua `referee_idempotency`,
// không double-score, không chồng chéo (fix gốc sự cố Karinox).
//   - Lỗi transient (mạng/timeout/5xx/520) → DỪNG drain giữ nguyên thứ tự,
//     thử lại sau 4s hoặc khi window 'online'.
//   - Lỗi nghiệp vụ 4xx → đánh dấu "failed" (hiện ở /referee/pending), tiếp
//     tục replay các event sau (lựa chọn 2a: thao tác trọng tài là nguồn chân lý).
import { api } from "./api";
import { listEvents, updateEvent, deleteEvent } from "./refereeOfflineStore";

const TRANSIENT_HTTP = new Set([0, 408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
const DRAIN_INTERVAL_MS = 4000;
const REQUEST_TIMEOUT_MS = 10000;

const listeners = new Set();
const _pendingByMatch = new Map(); // match_id -> count (pending + syncing)
let _started = false;
let _draining = false;

function emit(type, payload = {}) {
  for (const cb of listeners) {
    try { cb({ type, payload }); } catch { /* ignore */ }
  }
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function _bump(matchId, delta) {
  if (!matchId) return;
  const next = (_pendingByMatch.get(matchId) || 0) + delta;
  if (next > 0) _pendingByMatch.set(matchId, next);
  else _pendingByMatch.delete(matchId);
}

export function getPendingCount(matchId) {
  return _pendingByMatch.get(matchId) || 0;
}

export function hasPending(matchId) {
  return getPendingCount(matchId) > 0;
}

// Gọi bởi refereeApi ngay sau khi enqueueEvent thành công.
export function noteEnqueued(row) {
  _bump(row.match_id, 1);
  emit("event_enqueued", { id: row.id, match_id: row.match_id });
  kickDrain();
}

export function kickDrain() {
  setTimeout(() => { drainNow().catch(() => { /* noop */ }); }, 50);
}

export async function drainNow() {
  if (_draining) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  _draining = true;
  try {
    const all = await listEvents();
    const evs = all
      .filter((e) => e.status === "pending" || e.status === "syncing")
      .sort((a, b) => (a.local_ts || "").localeCompare(b.local_ts || ""));
    for (const ev of evs) {
      await updateEvent(ev.id, { status: "syncing" });
      const path = ev.endpoint.startsWith("/api/") ? ev.endpoint.slice(4) : ev.endpoint;
      try {
        const res = await api.request({
          url: path,
          method: ev.method || "POST",
          data: ev.body || {},
          headers: { "Idempotency-Key": ev.idem || ev.id },
          timeout: REQUEST_TIMEOUT_MS,
        });
        await deleteEvent(ev.id);
        _bump(ev.match_id, -1);
        emit("event_synced", { id: ev.id, match_id: ev.match_id, data: res.data });
      } catch (err) {
        const status = err?.response?.status || 0;
        const transient = err?.code === "ECONNABORTED" || TRANSIENT_HTTP.has(status);
        if (transient) {
          await updateEvent(ev.id, {
            status: "pending",
            attempts: (ev.attempts || 0) + 1,
            last_error: String(err?.message || err),
            last_error_status: status || null,
          });
          emit("event_retry_later", { id: ev.id, match_id: ev.match_id });
          return; // giữ FIFO — thử lại toàn bộ đuôi ở tick sau
        }
        // 4xx nghiệp vụ → failed, KHÔNG chặn các event sau (2a).
        const detail = err?.response?.data?.detail;
        await updateEvent(ev.id, {
          status: "failed",
          attempts: (ev.attempts || 0) + 1,
          last_error: typeof detail === "string" ? detail : JSON.stringify(detail || err?.message || "unknown"),
          last_error_status: status || null,
        });
        _bump(ev.match_id, -1);
        emit("event_failed", { id: ev.id, match_id: ev.match_id, status });
      }
      if (getPendingCount(ev.match_id) === 0) {
        emit("queue_drained", { match_id: ev.match_id });
      }
    }
  } finally {
    _draining = false;
  }
}

async function _hydrate() {
  const all = await listEvents();
  _pendingByMatch.clear();
  for (const e of all) {
    if (e.status === "pending" || e.status === "syncing") _bump(e.match_id, 1);
    // App crash giữa lúc gửi → reset "syncing" về "pending" (idem key giữ nguyên).
    if (e.status === "syncing") await updateEvent(e.id, { status: "pending" });
  }
}

export function startSyncEngine() {
  if (_started) return;
  _started = true;
  _hydrate()
    .then(() => { emit("hydrated", {}); kickDrain(); })
    .catch(() => { /* IndexedDB unavailable — chạy online-only */ });
  if (typeof window !== "undefined") {
    window.addEventListener("online", kickDrain);
    setInterval(() => {
      if (_pendingByMatch.size > 0) drainNow().catch(() => { /* noop */ });
    }, DRAIN_INTERVAL_MS);
  }
}

export async function forceDrainNow() {
  await drainNow();
  return { drained: true };
}

export async function retryEvent(id) {
  const all = await listEvents();
  const ev = all.find((e) => e.id === id);
  if (!ev) return null;
  if (ev.status !== "pending" && ev.status !== "syncing") {
    await updateEvent(id, { status: "pending" });
    _bump(ev.match_id, 1);
  }
  emit("event_retry", { id });
  kickDrain();
  return ev;
}

export async function dismissEvent(id) {
  const all = await listEvents();
  const ev = all.find((e) => e.id === id);
  try { await deleteEvent(id); } catch { /* noop */ }
  if (ev && (ev.status === "pending" || ev.status === "syncing")) {
    _bump(ev.match_id, -1);
    if (getPendingCount(ev.match_id) === 0) emit("queue_drained", { match_id: ev.match_id });
  }
  emit("event_dropped", { id });
}
